<template>
    <div class="mb-3 user-metadata-view">
        <div v-if="fetchingUserAdditionalStats">
            <p class="font-weight-bold">
                <span class="spinner-grow spinner-grow-sm mr-2" role="status" aria-hidden="true"></span>
                Fetching replies ...
            </p>
        </div>
        <button v-else-if="!tweetUserAdditionalStats" class="btn btn-primary"
                @click="requestAdditionalUserDataHandler()">
            Request additional user metadata
        </button>
        <div v-else>
            <h3>User Timeline Analysis</h3>
            <div class="row">
                <div class="col-5 t">
                    <strong>Num collected posts</strong>
                </div>
                <div class="col-7">
                    {{ tweetUserAdditionalStats.timeline_num_posts }}
                </div>
            </div>


            <strong>Post histogram</strong>

            <div v-if="'datasets' in tweetHistogramData">
                <div class="form-row">
                    <div class="col">
                        <date-range-picker ref="picker" :locale-data="datepickerLocale" v-model="dateRange"
                                           :timePicker="true"
                                           :timePicker24Hour="true" dateUtil="moment" style="width: 100%">
                            <template v-slot:input="picker" style="min-width: 350px;">
                                {{ picker.startDate | date }} - {{ picker.endDate | date }}
                            </template>
                        </date-range-picker>
                    </div>
                </div>
                <div class="form-row mt-1 mb-2">
                    <div class="col">
                        <div class="btn-group btn-group-toggle">
                            <button type="button" class="btn btn-primary" @click="setHistoryMode('hours')">
                                Hourly
                            </button>
                            <button type="button" class="btn btn-primary" @click="setHistoryMode('days')">
                                Daily
                            </button>
                        </div>
                    </div>

                    <div class="col">
                        <button type="button" class="form-control btn btn-warning" @click="resetDates()">
                            Reset dates
                        </button>
                    </div>
                </div>
                <tweet-histogram :chart-data="tweetHistogramData"></tweet-histogram>
            </div>
            <p v-else>No user post history</p>


            <strong>Hashtags</strong>
            <div v-if="tweetUserAdditionalStats.timeline_hashtags">
                <wordcloud
                        :data="tweetUserAdditionalStats.timeline_hashtags"
                        nameKey="name"
                        valueKey="value"
                        :color="wordCloudColours"
                        :rotate="wordCloudRotation"
                        :font-size="wordCloudFontSize"
                        :showTooltip="false">
                </wordcloud>
            </div>
            <p v-else>No hashtags</p>

            <strong>User mentions</strong>
            <div v-if="tweetUserAdditionalStats.timeline_user_mentions">
                <wordcloud
                        :data="tweetUserAdditionalStats.timeline_user_mentions"
                        nameKey="name"
                        valueKey="value"
                        :color="wordCloudColours"
                        :rotate="wordCloudRotation"
                        :font-size="wordCloudFontSize"
                        :showTooltip="false">
                </wordcloud>
            </div>
            <p v-else>No user mentions</p>

            <strong>Most retweeted users</strong>
            <div v-if="tweetUserAdditionalStats.timeline_retweets">
                <wordcloud
                        :data="tweetUserAdditionalStats.timeline_retweets"
                        nameKey="name"
                        valueKey="value"
                        :color="wordCloudColours"
                        :rotate="wordCloudRotation"
                        :font-size="wordCloudFontSize"
                        :showTooltip="false">
                </wordcloud>
            </div>
            <p v-else>No retweeted users</p>

            <strong>Tweet Locations</strong>
            <div v-if="tweetUserAdditionalStats.timeline_coordinates">
                <g-maps :coords="tweetUserAdditionalStats.timeline_coordinates"></g-maps>
            </div>
            <p v-else>No locations</p>

            <strong>Sources</strong>
            <ul v-if="tweetUserAdditionalStats.timeline_sources">
                <li v-for="source in tweetUserAdditionalStats.timeline_sources">{{ source.text }} <span
                        class="badge badge-info">{{ source.weight }}</span></li>
            </ul>
            <p v-else>No sources</p>

            <strong>Languages</strong>
            <ul v-if="tweetUserAdditionalStats.timeline_languages">
                <li v-for="language in tweetUserAdditionalStats.timeline_languages">{{ language.text }} <span
                        class="badge badge-info">{{ language.weight }}</span></li>
            </ul>
            <p v-else>No languages detected</p>


        </div>
    </div>
</template>

<script>
    import {mapState, mapActions} from 'vuex'
    import DateRangePicker from 'vue2-daterange-picker'
    import wordcloud from 'vue-wordcloud'
    import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
    import TweetHistogram from "./TweetHistogram";
    import GMaps from "./GMaps";

    const moment = require('moment');

    export default {
        name: "TweetUserAdditionalData",
        data: function () {
            return {
                dateRange: {
                    startDate: moment().subtract(10, 'd'),
                    endDate: moment()
                },
                datepickerLocale: {
                    direction: 'ltr',
                    format: 'mm/dd/yyyy hh:MM',
                    separator: ' - ',
                    applyLabel: 'Apply',
                    cancelLabel: 'Cancel',
                    weekLabel: 'W',
                    customRangeLabel: 'Custom Range',
                    daysOfWeek: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
                    monthNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                    firstDay: 0
                },
                historyMode: "days",
                wordCloudColours: ['#1f77b4'],
                wordCloudRotation: {from: -0, to: 0, numOfOrientation: 1},
                wordCloudFontSize: [24, 70]
            }
        },
        computed: {
            ...mapState(["tweetUserAdditionalStats", "fetchingUserAdditionalStats"]),
            tweetHistogramData() {

                if (!this.tweetUserAdditionalStats)
                    return {};

                const histogramData = this.historyMode === "days" ? this.tweetUserAdditionalStats.timeline_histogram_days : this.tweetUserAdditionalStats.timeline_histogram_hours;
                let outputData = [];
                for (const i in histogramData) {
                    const date = moment(histogramData[i][0]);
                    const count = histogramData[i][1];

                    const filterStartDate = moment(this.dateRange.startDate);
                    const filterEndDate = moment(this.dateRange.endDate);
                    if (date >= filterStartDate && date <= filterEndDate)
                        outputData.push({x: date, y: count});
                }

                return {
                    datasets: [
                        {
                            data: outputData,
                            backgroundColor: '#005cbf',
                        }
                    ]
                };

            }
        },
        methods: {
            ...mapActions(["requestAdditionalUserData"]),
            requestAdditionalUserDataHandler() {
                this.requestAdditionalUserData();
            },
            resetDates() {
                this.dateRange = {
                    startDate: moment().subtract(10, 'd'),
                    endDate: moment()
                }
            },
            setHistoryMode(value) {
                this.historyMode = value;
            },
        },
        components: {
            GMaps,
            TweetHistogram,
            DateRangePicker,
            wordcloud,

        },
        filters: {
            date(dateVal) {
                return moment(dateVal).format("DD/MM/YYYY HH:mm");
            }
        }


    }
</script>

<style scoped>

</style>
